import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { type BillingTier, billingTierLabels } from '~/common/dataRequestUtils';
import { type DataRequestV2, ProjectGeometryType } from '~/common/types';
import Input from '~/components2/forms/Input';
import TextArea from '~/components2/forms/TextArea';
import { calculateArea, calculateLength } from '~/helpers/unitHelpers';
import FormSection from '~/pages/admin/civilsearch/common/FormSection';
import type { TProject } from './common';
import OpenProject from './OpenProject';

export const Separator = () => {
  return <hr className="border-b border-t-0" />;
};

export default function Details({ project, request }: { project: TProject; request: DataRequestV2 }) {
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const projectDims = useMemo(() => {
    const dims = {
      length: { label: 'Length', value: '' },
      area: { label: 'Area', value: '' },
    };

    if (!project) return dims;

    const area = calculateArea({
      unitSystem: 'imperial',
      unitType: 'acres',
      geometry: project.geometry,
    });

    dims.area.value = `${area.value} ${area.unit}`;

    if (project?.geometryType === ProjectGeometryType.Linear) {
      const ml = project.linear!.geometry as GeoJSON.MultiLineString;
      const l = calculateLength({
        unitSystem: 'imperial',
        unitType: 'miles',
        geometry: ml,
      });

      dims.length.value = `${l.value} ${l.unit}`;
    }

    return dims;
  }, [project?.id]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const fullAddress = useMemo(() => {
    if (!project) return '';
    return `${project?.address2.street}, ${project.address2.city}, ${project.address2.state} ${project.address2.zipCode}`;
  }, [project?.id]);

  if (!project) return null;

  return (
    <div className="flex flex-col space-y-6">
      <FormSection title="Project Overview" headerChildren={<OpenProject id={project.id} />}>
        <div className="grid grid-cols-2 gap-4">
          <Input value={project.projectId} label="Project ID" canCopy disabled />
          <Input value={project.name} label="Project Name" canCopy disabled />

          <Input value={project.organization.name} label="Organization Name" canCopy disabled />

          <Input value={projectDims.area.value} label={projectDims.area.label} canCopy disabled />
          <Input value={projectDims.length.value} label={projectDims.length.label} canCopy disabled />

          <Input
            value={billingTierLabels[project.billingTier as BillingTier] ?? project.billingTier}
            label={'Project Type'}
            canCopy
            disabled
          />

          <Input value={project.billingCode} label={'Billing Code 1'} canCopy disabled />
          <Input value={project.billingCode2} label={'Billing Code 2'} canCopy disabled />

          <Input value={project.isBillable ? 'Yes' : 'No'} label={'Is Billable?'} canCopy disabled />
        </div>
      </FormSection>

      <Separator />

      <FormSection title="Project Team">
        <div className="grid grid-cols-2 gap-4">
          <Input value={project.projectManager.name} label={'PM Name'} canCopy disabled />

          <Input value={project.projectManager.email} label={'PM Email'} canCopy disabled />
        </div>
      </FormSection>

      <Separator />

      <FormSection title="Requestor Info">
        <div className="grid grid-cols-2 gap-4">
          <Input value={request.createdBy.name} label="Created By Name" canCopy disabled />

          <Input value={request.createdBy.email} label="Created By Email" canCopy disabled />
        </div>
      </FormSection>

      <Separator />

      <FormSection title="Project Narrative">
        <div className="grid grid-cols-2 gap-4">
          <Input value={project.client} label={'Client'} canCopy disabled />
          <Input value={project.type} label={'Project Type'} canCopy disabled />

          <TextArea value={project.scope} label={'Scope'} canCopy disabled />
          <TextArea value={project.projectVicinity} label={'Vicinity'} canCopy disabled />

          <Input value={project.peName} label={'PE/PG/PLS Name'} canCopy disabled />
          <Input value={project.peLicenseNumber} label={'PE License'} canCopy disabled />
          <Input value={project.peLicenseState} label={'PE State'} canCopy disabled />
        </div>
      </FormSection>

      <Separator />

      <FormSection title="Project Location">
        <div className="grid grid-cols-3 gap-4">
          <Input value={fullAddress} label={'Full Address'} canCopy disabled />
          <Input value={project.address2.street} label={'Street'} canCopy disabled />
          <Input value={project.address2.crossStreets} label={'Cross Street'} canCopy disabled />
          <Input value={project.address2.city} label={'City'} canCopy disabled />
          <Input value={project.address2.state} label={'State'} canCopy disabled />

          <Input value={project.address2.zipCode} label={'Zip'} canCopy disabled />
        </div>
      </FormSection>

      <Separator />

      <FormSection title="Project Timelines">
        <div className="grid grid-cols-2 gap-4">
          <Input value={project.startDate?.toLocaleString(DateTime.DATE_MED)} label="Start Date" canCopy disabled />

          <Input value={project.endDate?.toLocaleString(DateTime.DATE_MED)} label="End Date" canCopy disabled />

          <Input
            value={project.constructionStartDate?.toLocaleString(DateTime.DATE_MED)}
            label="Construction Start Date"
            canCopy
            disabled
          />

          <Input
            value={project.constructionEndDate?.toLocaleString(DateTime.DATE_MED)}
            label="Construction End Date"
            canCopy
            disabled
          />

          <Input value={project.createdAt.toLocaleString(DateTime.DATE_MED)} label="Created On" canCopy disabled />
        </div>
      </FormSection>
    </div>
  );
}
